import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { mockMemorials } from '../data/mockData';
import ExploreFilters from '../components/Explore/ExploreFilters';
import FeaturedCampaignsWidget from '../components/Explore/FeaturedCampaignsWidget';
import { writeFetch } from '../api';

const {
  FiSearch, FiBookOpen, FiArrowRight, FiMapPin, FiHeart,
  FiFilter, FiX, FiRefreshCw, FiLoader
} = FiIcons;

// Normalise a DB record into the same shape as mockMemorials
const normaliseDbMemorial = (page, lovedOnesMap) => {
  const lo = lovedOnesMap[page.loved_one_id] || {};
  const firstName = lo.first_name || '';
  const lastName = lo.last_name || '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ') || page.title || 'Unknown';
  return {
    id: `db-${page.id}`,
    dbPageId: page.id,
    name: fullName,
    birthDate: lo.date_of_birth || '1900-01-01',
    deathDate: lo.date_of_passing || new Date().toISOString().slice(0, 10),
    isPrivate: page.privacy_setting !== 'public',
    category: 'General',
    location: lo.birth_place || lo.resting_place || 'Unknown',
    tagline: page.description || lo.biography || 'A life beautifully lived.',
    tributeCount: 0,
    coverImage: lo.cover_image || 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?q=80&w=2070&auto=format&fit=crop',
    profileImage: lo.profile_image || 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=1288&auto=format&fit=crop',
    _isFromDb: true,
  };
};

const Explore = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ category: 'All', dateRange: 'all', location: '' });
  const [dbMemorials, setDbMemorials] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMemorials = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch published memorial pages
      const pagesRes = await writeFetch('read/memorial_pages?is_published=1&limit=100');
      const pagesData = await pagesRes.json();
      const pages = pagesData?.data || [];

      if (pages.length === 0) { setDbMemorials([]); return; }

      // Fetch all loved_ones
      const loRes = await writeFetch('read/loved_ones?limit=100');
      const loData = await loRes.json();
      const lovedOnes = loData?.data || [];
      const lovedOnesMap = {};
      lovedOnes.forEach(lo => { lovedOnesMap[lo.id] = lo; });

      const normalised = pages.map(p => normaliseDbMemorial(p, lovedOnesMap));
      setDbMemorials(normalised);
    } catch (err) {
      console.error('Failed to fetch memorials:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMemorials();
    const handler = () => fetchMemorials();
    window.addEventListener('memorial-created', handler);
    return () => window.removeEventListener('memorial-created', handler);
  }, [fetchMemorials]);

  const handleFilterChange = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));
  const resetFilters = () => { setFilters({ category: 'All', dateRange: 'all', location: '' }); setSearchTerm(''); };

  // Merge: DB memorials first, then mock (deduped by name)
  const allMemorials = useMemo(() => {
    const dbNames = new Set(dbMemorials.map(m => m.name.toLowerCase()));
    const filteredMock = mockMemorials.filter(m => !dbNames.has(m.name.toLowerCase()));
    return [...dbMemorials, ...filteredMock];
  }, [dbMemorials]);

  const filteredMemorials = useMemo(() => {
    return allMemorials.filter(m => {
      const matchesSearch = m.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = filters.category === 'All' || m.category === filters.category;
      const matchesLocation = !filters.location || (m.location || '').toLowerCase().includes(filters.location.toLowerCase());

      let matchesDate = true;
      const deathYear = new Date(m.deathDate).getFullYear();
      if (filters.dateRange === 'recent') matchesDate = deathYear >= 2023;
      else if (filters.dateRange === '2020s') matchesDate = deathYear >= 2020;
      else if (filters.dateRange === '2010s') matchesDate = deathYear >= 2010 && deathYear <= 2019;
      else if (filters.dateRange === 'legacy') matchesDate = deathYear < 2010;

      return matchesSearch && matchesCategory && matchesLocation && matchesDate;
    });
  }, [allMemorials, searchTerm, filters]);

  return (
    <div className="pt-32 min-h-screen bg-brand-light pb-20">
      <div className="container mx-auto px-6">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-7xl font-serif text-accent-navy mb-6"
          >
            Explore Memorials
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-text-muted text-xl font-light"
          >
            Find a loved one to honor their story, leave a message, or light a virtual candle.
          </motion.p>
        </div>

        <div className="flex flex-col lg:flex-row gap-10">
          <div className="lg:w-1/4 hidden lg:block space-y-6">
            <ExploreFilters filters={filters} onFilterChange={handleFilterChange} onReset={resetFilters} />
            <FeaturedCampaignsWidget />
          </div>

          <div className="lg:w-3/4 space-y-8">
            <div className="flex gap-4">
              <div className="relative flex-grow group">
                <SafeIcon icon={FiSearch} className="absolute left-6 top-1/2 transform -translate-y-1/2 text-text-muted w-6 h-6 group-focus-within:text-accent-gold transition-colors" />
                <input
                  type="text"
                  placeholder="Search by name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-16 pr-8 py-5 rounded-2xl border-2 border-transparent shadow-xl focus:ring-4 focus:ring-accent-gold/10 focus:border-accent-gold bg-white text-text-main text-lg transition-all outline-none"
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`lg:hidden p-5 rounded-2xl border-2 transition-all ${showFilters ? 'bg-accent-navy border-accent-navy text-white' : 'bg-white border-brand-dark text-accent-navy'}`}
              >
                <SafeIcon icon={showFilters ? FiX : FiFilter} className="w-6 h-6" />
              </button>
            </div>

            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="lg:hidden overflow-hidden space-y-6"
                >
                  <ExploreFilters filters={filters} onFilterChange={handleFilterChange} onReset={resetFilters} />
                  <FeaturedCampaignsWidget />
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex flex-wrap items-center justify-between gap-4 py-2">
              <div className="flex items-center gap-3">
                {loading ? (
                  <div className="flex items-center gap-2 text-text-muted text-sm font-bold">
                    <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin text-accent-gold" />
                    Loading memorials...
                  </div>
                ) : (
                  <p className="text-sm font-bold text-text-muted uppercase tracking-widest">
                    Showing {filteredMemorials.length} memorial{filteredMemorials.length !== 1 ? 's' : ''}
                    {dbMemorials.length > 0 && (
                      <span className="ml-2 text-accent-gold">· {dbMemorials.length} from community</span>
                    )}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                {filters.category !== 'All' && (
                  <span className="bg-brand-dark/50 text-accent-navy text-[10px] font-black px-3 py-1 rounded-full flex items-center gap-2">
                    {filters.category} <FiX className="cursor-pointer" onClick={() => handleFilterChange('category', 'All')} />
                  </span>
                )}
                {filters.dateRange !== 'all' && (
                  <span className="bg-brand-dark/50 text-accent-navy text-[10px] font-black px-3 py-1 rounded-full flex items-center gap-2">
                    {filters.dateRange} <FiX className="cursor-pointer" onClick={() => handleFilterChange('dateRange', 'all')} />
                  </span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <AnimatePresence mode="popLayout">
                {filteredMemorials.map((memorial) => (
                  <motion.div
                    layout
                    key={memorial.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Link to={`/memorial/${memorial.id}`} className="group block h-full">
                      <div className="bg-white rounded-[40px] overflow-hidden shadow-sm border border-brand-dark h-full flex flex-col transition-all duration-500 hover:shadow-2xl hover:-translate-y-2">
                        <div className="h-56 relative overflow-hidden">
                          <img src={memorial.coverImage} alt={memorial.name} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                          <div className="absolute top-6 right-6 flex flex-col gap-2 items-end">
                            <div className="bg-white/20 backdrop-blur-md text-white text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-full border border-white/20">
                              {memorial._isFromDb ? 'Community' : memorial.category}
                            </div>
                            <div className="bg-accent-gold text-white text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-full shadow-lg">
                              {new Date(memorial.deathDate).getFullYear()}
                            </div>
                          </div>
                          <div className="absolute -bottom-8 left-8 w-20 h-20 rounded-3xl border-4 border-white overflow-hidden shadow-xl z-10 transform transition-transform group-hover:rotate-3">
                            <img src={memorial.profileImage} alt="" className="w-full h-full object-cover" />
                          </div>
                        </div>

                        <div className="pt-12 px-8 pb-8 flex-grow flex flex-col">
                          <div className="flex items-center gap-2 text-accent-gold text-[9px] font-black uppercase tracking-widest mb-2">
                            <SafeIcon icon={FiMapPin} className="w-3 h-3" /> {memorial.location || 'Unknown'}
                          </div>
                          <h3 className="font-serif text-2xl text-accent-navy mb-1 group-hover:text-accent-gold transition-colors">{memorial.name}</h3>
                          <p className="text-xs font-bold text-text-muted mb-6">
                            {new Date(memorial.birthDate).getFullYear()} — {new Date(memorial.deathDate).getFullYear()}
                          </p>
                          <p className="text-text-muted line-clamp-2 italic mb-8 font-light text-sm leading-relaxed">"{memorial.tagline}"</p>
                          <div className="mt-auto pt-6 border-t border-brand-dark flex justify-between items-center text-[10px] font-black uppercase tracking-widest text-text-muted">
                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-2 group-hover:text-accent-navy transition-colors">
                                <SafeIcon icon={FiBookOpen} className="w-4 h-4 text-accent-gold" />
                                <span>View Sanctuary</span>
                              </div>
                              <div className="flex items-center gap-1.5 text-accent-gold">
                                <SafeIcon icon={FiHeart} className="w-3.5 h-3.5 fill-accent-gold/20" />
                                <span>{memorial.tributeCount || 0} Tributes</span>
                              </div>
                            </div>
                            <SafeIcon icon={FiArrowRight} className="w-4 h-4 transition-transform group-hover:translate-x-2" />
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {!loading && filteredMemorials.length === 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-32 bg-white rounded-[50px] border-2 border-dashed border-brand-dark"
              >
                <SafeIcon icon={FiSearch} className="w-20 h-20 text-text-muted/10 mx-auto mb-6" />
                <h3 className="text-3xl font-serif text-accent-navy mb-2">No memorials found</h3>
                <p className="text-text-muted font-light mb-8 max-w-sm mx-auto">Try adjusting your filters or search term to discover other beautiful legacies.</p>
                <button
                  onClick={resetFilters}
                  className="text-accent-gold font-black uppercase tracking-widest text-xs hover:text-accent-navy transition-colors flex items-center gap-2 mx-auto"
                >
                  <SafeIcon icon={FiRefreshCw} /> Clear All Filters
                </button>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Explore;